/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedData').BalancedData} BalancedData */

import { useEffect, useMemo, useState } from 'react'
import BalancedFormHeader from './BalancedFormHeader'
import BalancedFormTree from './BalancedFormTree'
import BalancedFormColumns from './BalancedFormColumns'
import BalancedFormJobInfo from './BalancedFormJobInfo'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { buildSuccessorsMap, canInsertBefore, computeNodeGroups, getPositionRange } from '../../generic/utilities/objectStructures/balanced/balancedConstraints'
import { allNodesSupportedByDevice, appendNodesToColumn, isExternalJobNode, placeableJobNodes, removeNodesFromColumns, toggleMultiSelection } from '../../generic/utilities/objectStructures/balanced/balancedColumns'
import { remapColumns } from '../../generic/utilities/objectStructures/balanced/balancedRemap'
import { Group, Panel } from 'react-resizable-panels'
import PanelSeparator from '../../generic/PanelSeparator'
import { FULL_HEIGHT } from '../../generic/utilities/size_utils'
import { Box, CircularProgress, Typography } from '@mui/material'

/**
 * Rebuilds the columnNodeIds map from a nodes array.
 *
 * Only JOB nodes with a non-null `jobOrder` are included. In TYPE mode the column
 * key is `positionInLine`; in LINE mode it is the `preferenceDevice` id. Nodes
 * within each column are sorted ascending by `jobOrder`.
 *
 * Story 146 / S66 (RN30.1, D30): an EXTERNAL JOB node never gets a column, not even when the
 * loaded data still carries the `jobOrder`/`positionInLine` it had before it was marked.
 *
 * @param {import('../../generic/utilities/objectStructures/balanced/BalancedNode').BalancedNode[]|null} nodes
 * @param {'TYPE'|'LINE'|null} balancedType
 * @returns {Object} Map of columnKey → nodeId[]
 */
const buildColumnNodeIdsFromNodes = (nodes, balancedType) => {
  if (!nodes) return {}
  const assigned = nodes.filter(n => n.nodeType === 'JOB' && !isExternalJobNode(n) && n.jobReference?.jobOrder != null)
  const map = {}
  for (const node of assigned) {
    let columnKey
    if (balancedType === 'TYPE') {
      columnKey = String(node.jobReference.positionInLine)
    } else if (balancedType === 'LINE') {
      columnKey = node.jobReference.preferenceDevice?.id != null
        ? String(node.jobReference.preferenceDevice.id)
        : null
    }
    if (columnKey == null) continue
    if (!map[columnKey]) map[columnKey] = []
    map[columnKey].push(node)
  }
  for (const key of Object.keys(map)) {
    map[key] = map[key].sort((a, b) => (a.jobReference.jobOrder ?? 0) - (b.jobReference.jobOrder ?? 0)).map(n => n.id)
  }
  return map
}

/**
 * Main content area of the balanced view.
 * Renders the filter header, the job-node tree panel and the assignment columns.
 *
 * @param {Object}        props
 * @param {Function}      props.dispatchBalancedData - Reducer dispatcher from `Balanced`
 * @param {BalancedData}  props.balancedData         - Shared balanced state from `Balanced`
 * @param {number}        props.revertKey            - Bumped on every revert so this component rebuilds local state from the snapshot
 * @param {Function}      props.showAlert            - Alert display function injected from `Balanced`
 * @param {boolean}       props.loading              - When true, shows a loading indicator instead of the panels
 * @param {DeviceGroupDevice[]} props.deviceGroupDevices - Devices of the selected line (LINE mode), owned by `Balanced`
 * @param {Function}      props.onDevicesLoaded      - Setter called by the header when a line's devices are loaded
 * @param {Function}      props.onGroupResolved      - Setter called by the header with the position group in force
 */
const BalancedForm = ({ dispatchBalancedData, balancedData, revertKey, showAlert, loading = false, deviceGroupDevices = [], deviceGroupLineId = null, onDevicesLoaded, onGroupResolved }) => {
  const { t_ } = useSmartTranslation()
  const [columnNodeIds, setColumnNodeIds] = useState({})
  const [confirmDialog, setConfirmDialog] = useState({ open: false, pendingAction: null })
  // Bumped by a user selector change to request a remap once the new context (and,
  // for LINE, its devices) is ready (HU-12).
  const [remapRequest, setRemapRequest] = useState(0)
  const [draggingNode, setDraggingNode] = useState(null)
  const [selectedNode, setSelectedNode] = useState(null)
  // Ids of JOB nodes picked with Ctrl/Cmd+click for a group drag (HU-04/09).
  const [multiSelection, setMultiSelection] = useState(new Set())
  // Per-session collapse state of the info panel's Instructions/Notes sections
  // (HU-08). Kept here so it survives the per-selection remount of the info panel.
  const [infoCollapsed, setInfoCollapsed] = useState({ documents: false, instructions: false, notes: false })
  // Width (px) of the right info panel; resizable via its left-edge handle.
  const [infoWidth, setInfoWidth] = useState(288)

  /**
   * Starts a drag on the info panel's left-edge handle. Dragging left widens the
   * info panel (the main region shrinks); only a minimum width is enforced.
   *
   * @param {React.MouseEvent} e
   */
  const startInfoResize = (e) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = infoWidth
    const onMove = (ev) => setInfoWidth(Math.max(220, startWidth - (ev.clientX - startX)))
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  /**
   * Toggles the collapsed state of an info-panel section (Instructions/Notes).
   *
   * @param {'instructions'|'notes'} section
   */
  const toggleInfoCollapse = (section) => setInfoCollapsed(prev => ({ ...prev, [section]: !prev[section] }))

  /**
   * Toggles a JOB node in the multi-selection. Ignores non-JOB nodes so parents
   * are never part of the group (S15). On the first pick the currently
   * info-selected JOB is seeded in so it joins the group to move.
   *
   * @param {Object} node
   */
  const toggleMultiSelect = (node) => {
    if (node?.nodeType !== 'JOB') return
    const seedId = selectedNode?.nodeType === 'JOB' ? selectedNode.id : null
    setMultiSelection(prev => toggleMultiSelection(prev, node.id, seedId))
  }

  /**
   * Plain (info) selection: sets the shared selectedNode and clears the group
   * multi-selection so the two gestures do not interfere.
   *
   * @param {Object|null} node
   */
  const handleSelectNode = (node) => {
    setSelectedNode(node)
    setMultiSelection(new Set())
  }

  const { nodesSnapshot, balancedType } = balancedData

  const augmentedNodes = useMemo(() => computeNodeGroups(balancedData.nodes ?? []), [balancedData.nodes])
  const augmentedBalancedData = useMemo(() => ({ ...balancedData, nodes: augmentedNodes }), [balancedData, augmentedNodes])

  const nodeMap = Object.fromEntries(augmentedNodes.map(n => [n.id, n]))

  // Line-advancement context, computed on the RAW backend nodes (real groupIds),
  // never on the computeNodeGroups-augmented nodes (synthetic depth keys).
  const rawNodeById = useMemo(
    () => Object.fromEntries((balancedData.nodes ?? []).filter(n => n.nodeType === 'JOB').map(n => [n.id, n])),
    [balancedData.nodes]
  )
  const successorsById = useMemo(() => buildSuccessorsMap(balancedData.nodes ?? []), [balancedData.nodes])

  /**
   * Resolves a column key to its line position.
   * TYPE: the column number. LINE: the device `positionInLine`.
   *
   * @param {string} columnKey
   * @returns {number|null}
   */
  const positionOfColumn = (columnKey) => {
    if (balancedType === 'TYPE') {
      const n = parseInt(columnKey, 10)
      return isNaN(n) ? null : n
    }
    if (balancedType === 'LINE') {
      const device = deviceGroupDevices.find(d => String(d.id) === String(columnKey))
      return device?.positionInLine ?? null
    }
    return null
  }

  // Position of every currently placed node, keyed by node id.
  const positionByNodeId = useMemo(() => {
    const map = {}
    for (const [columnKey, ids] of Object.entries(columnNodeIds)) {
      let position
      if (balancedType === 'TYPE') {
        const n = parseInt(columnKey, 10)
        position = isNaN(n) ? null : n
      } else if (balancedType === 'LINE') {
        position = deviceGroupDevices.find(d => String(d.id) === String(columnKey))?.positionInLine ?? null
      }
      if (position == null) continue
      for (const id of ids) map[id] = position
    }
    return map
  }, [columnNodeIds, balancedType, deviceGroupDevices])

  const positionCtx = useMemo(
    () => ({ nodeById: rawNodeById, positionByNodeId, successorsById }),
    [rawNodeById, positionByNodeId, successorsById]
  )

  /**
   * Whether a node's line-advancement range allows the given target column.
   * Resolves the node to its raw version so the real backend groups are used.
   *
   * @param {string} columnKey
   * @param {Object} node - Dragged node (payload) or raw node
   * @returns {boolean}
   */
  const columnPositionAllows = (columnKey, node) => {
    const position = positionOfColumn(columnKey)
    if (position == null) return false
    const rawNode = rawNodeById[node?.id] ?? node
    const { min, max } = getPositionRange(rawNode, positionCtx)
    return position >= min && position <= max
  }

  /**
   * Validates the line-advancement range for a node dropping on a target column.
   * When out of range, shows an explanatory alert (min or max violation) and
   * returns false; returns true when the position is allowed.
   *
   * @param {Object} node - Dragged node (payload) or raw node
   * @param {string} targetColumnKey
   * @returns {boolean}
   */
  const checkPositionOrAlert = (node, targetColumnKey) => {
    const position = positionOfColumn(targetColumnKey)
    const rawNode = rawNodeById[node.id] ?? node
    const { min, max } = getPositionRange(rawNode, positionCtx)
    if (position == null || position < min) {
      showAlert?.({ severity: 'error', title: t_('This node cannot be placed in position {{position}}: it must be at position {{min}} or later, after its preceding operations', { position: position ?? '?', min }) })
      return false
    }
    if (position > max) {
      showAlert?.({ severity: 'error', title: t_('This node cannot be placed in position {{position}}: it must be at position {{max}} or earlier, before its following operations', { position, max }) })
      return false
    }
    return true
  }

  /**
   * Sets the shared drag state so column components can compute drop feedback.
   * @param {Object} node - The dragged node (or group payload with `_isGroup: true`)
   */
  const handleNodeDragStart = (node) => setDraggingNode(node)
  /** Clears the shared drag state when a drag operation ends. */
  const handleNodeDragEnd = () => setDraggingNode(null)

  useEffect(() => {
    if (revertKey > 0) {
      setColumnNodeIds(buildColumnNodeIdsFromNodes(nodesSnapshot, balancedType))
    }
  }, [revertKey, nodesSnapshot, balancedType])

  const workflowId = balancedData.workflow?.id

  // Builds the columns from the loaded nodes. On a TYPE<->LINE switch it does a
  // transient rebuild from the snapshot that the remap effect below then corrects.
  useEffect(() => {
    setColumnNodeIds(buildColumnNodeIdsFromNodes(nodesSnapshot, balancedType))
  }, [workflowId, nodesSnapshot, balancedType])

  /**
   * Writes the current column arrangement back into each node's `jobReference`.
   *
   * Position within a column becomes 1-based `jobOrder`. In TYPE mode also sets
   * `positionInLine` from the column key; in LINE mode also sets `preferenceDevice`
   * from the loaded devices list.
   *
   * @param {Object} updatedColumnNodeIds - Map of columnKey → nodeId[] in the desired order
   */
  const syncColumnJobOrders = (updatedColumnNodeIds) => {
    const updates = []
    for (const [columnKey, ids] of Object.entries(updatedColumnNodeIds)) {
      ids.forEach((id, i) => {
        const fields = { jobOrder: i + 1 }
        if (balancedData.balancedType === 'TYPE') {
          fields.positionInLine = parseInt(columnKey, 10)
          // A TYPE placement is not tied to a device; clear any stale one so the
          // line position is read from positionInLine (not an old preferenceDevice).
          fields.preferenceDevice = null
        } else if (balancedData.balancedType === 'LINE') {
          const device = deviceGroupDevices.find(d => String(d.id) === columnKey)
          fields.preferenceDevice = device ?? null
        }
        updates.push({ nodeId: id, fields })
      })
    }
    dispatchBalancedData({ type: 'BULK_UPDATE_NODE_JOB_REFERENCES', updates })
  }

  // Remap the current assignment onto a new context after a user selector change
  // (HU-12) instead of clearing it. Runs only when a remap was requested; for LINE
  // it waits until the new line's devices are loaded (deviceGroupLineId matches).
  // The `remapRequest === 0` guard makes re-runs (e.g. from the node dispatch below)
  // no-ops, so listing balancedData.nodes as a dependency is loop-safe.
  useEffect(() => {
    if (remapRequest === 0) return
    // No concrete target yet (e.g. right after switching mode) → nothing to remap.
    if (balancedType === 'TYPE' && Number.isNaN(parseInt(balancedData.selectedType, 10))) return
    if (balancedType === 'LINE' && !balancedData.selectedType?.id) return
    // For LINE, wait until the new line's devices are loaded before remapping.
    if (balancedType === 'LINE' && deviceGroupLineId !== balancedData.selectedType.id) return

    const nodes = balancedData.nodes ?? []
    const context = {
      balancedType,
      positionsCount: balancedType === 'TYPE' ? parseInt(balancedData.selectedType, 10) : undefined,
      devices: deviceGroupDevices
    }
    const { columnNodeIds: remapped, unassigned } = remapColumns(nodes, context)
    setColumnNodeIds(remapped)

    // Sync nodes: kept jobs get jobOrder + positionInLine (TYPE) / preferenceDevice
    // (LINE); jobs no longer placed are cleared.
    const updates = []
    const keptIds = new Set()
    for (const [columnKey, ids] of Object.entries(remapped)) {
      ids.forEach((id, i) => {
        keptIds.add(id)
        const fields = { jobOrder: i + 1 }
        if (balancedType === 'TYPE') {
          fields.positionInLine = parseInt(columnKey, 10)
          fields.preferenceDevice = null
        } else if (balancedType === 'LINE') {
          fields.preferenceDevice = deviceGroupDevices.find(d => String(d.id) === columnKey) ?? null
        }
        updates.push({ nodeId: id, fields })
      })
    }
    for (const n of nodes) {
      if (n.nodeType === 'JOB' && n.jobReference?.jobOrder != null && !keptIds.has(n.id)) {
        updates.push({ nodeId: n.id, fields: { jobOrder: null, positionInLine: null, preferenceDevice: null } })
      }
    }
    if (updates.length) dispatchBalancedData({ type: 'BULK_UPDATE_NODE_JOB_REFERENCES', updates })

    if (unassigned.length > 0) {
      showAlert?.({
        severity: 'warning',
        title: t_('{{count}} operation(s) were unassigned by the line/type change:', { count: unassigned.length }),
        message: unassigned.join(', ')
      })
    }
    setRemapRequest(0)
  }, [remapRequest, balancedType, balancedData.selectedType, balancedData.nodes, deviceGroupDevices, deviceGroupLineId, showAlert, t_, dispatchBalancedData])

  /**
   * Drops a group of nodes onto a target column (all-or-nothing).
   *
   * Accepts both unassigned nodes (assigned for the first time) and already-placed
   * nodes (relocated between columns). In LINE mode every candidate's technology
   * must be supported by the target device, and every candidate's line-advancement
   * position must be allowed; if any candidate fails, the whole group is rejected
   * with an alert and nothing moves (S13). Candidates keep their `order` and are
   * appended after the column's current cards.
   *
   * @param {BalancedNode[]} nodes - Group nodes from the drag payload (`_nodes`)
   * @param {string} targetColumnKey - Key of the column receiving the drop
   */
  const handleGroupColumnDrop = (nodes, targetColumnKey) => {
    const candidates = [...new Map((nodes ?? []).map(n => [n.id, n])).values()]
    if (!candidates.length) return

    if (balancedData.balancedType === 'LINE') {
      const device = deviceGroupDevices.find(d => String(d.id) === targetColumnKey)
      if (!allNodesSupportedByDevice(candidates, device)) {
        showAlert?.({ severity: 'error', message: t_('Not all node technologies are supported by this device') })
        setDraggingNode(null)
        return
      }
    }

    for (const candidate of candidates) {
      if (!checkPositionOrAlert(candidate, targetColumnKey)) {
        setDraggingNode(null)
        return
      }
    }

    const sorted = [...candidates].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    const updated = appendNodesToColumn(columnNodeIds, sorted.map(n => n.id), targetColumnKey)
    setColumnNodeIds(updated)
    syncColumnJobOrders(updated)
    setDraggingNode(null)
    setMultiSelection(new Set())
  }

  /**
   * Unassigns a group of nodes dropped back onto the tree: removes them from any
   * column and clears their balancing fields (S12).
   *
   * @param {BalancedNode[]} nodes - Group nodes from the drag payload (`_nodes`)
   */
  const handleGroupDroppedOnTree = (nodes) => {
    const ids = (nodes ?? []).map(n => n.id)
    if (!ids.length) return
    const updated = removeNodesFromColumns(columnNodeIds, ids)
    setColumnNodeIds(updated)
    syncColumnJobOrders(updated)
    ids.forEach(id => dispatchBalancedData({
      type: 'UPDATE_NODE_JOB_REFERENCE',
      nodeId: id,
      fields: { jobOrder: null, positionInLine: null, preferenceDevice: null }
    }))
    setDraggingNode(null)
    setMultiSelection(new Set())
  }

  /**
   * Handles a single-node or group drop onto a column.
   *
   * Delegates group drops to {@link handleGroupColumnDrop}. For single nodes,
   * removes the node from its source column (if any), inserts it before
   * `insertBeforeId` in the target column, then synchronises job orders.
   *
   * Story 146 / S66 (RN30.1, D30): this is the last gate into a column, so an EXTERNAL JOB is
   * dropped here whatever the drag payload carries — alone the drop is ignored, and inside a group
   * only the internal jobs of that group are placed.
   *
   * @param {Object} node - Dragged node (may carry `_isGroup` and `_sourceColumnKey`)
   * @param {string} targetColumnKey - Destination column key
   * @param {number|null} [insertBeforeId] - Insert before this node id; null = append
   */
  const handleColumnDrop = (node, targetColumnKey, insertBeforeId = null) => {
    if (node._isGroup) {
      handleGroupColumnDrop(placeableJobNodes(node._nodes), targetColumnKey)
      return
    }
    if (isExternalJobNode(node)) {
      setDraggingNode(null)
      return
    }

    const sourceColumnKey = node._sourceColumnKey ?? null
    const nodeId = node.id

    if (!checkPositionOrAlert(node, targetColumnKey)) {
      setDraggingNode(null)
      return
    }

    const orderedTarget = (columnNodeIds[targetColumnKey] ?? []).map(id => nodeMap[id]).filter(Boolean)
    if (!canInsertBefore(node, orderedTarget, insertBeforeId)) return

    const updated = { ...columnNodeIds }
    if (sourceColumnKey) {
      updated[sourceColumnKey] = (updated[sourceColumnKey] ?? []).filter(id => id !== nodeId)
    }
    const existing = (updated[targetColumnKey] ?? []).filter(id => id !== nodeId)
    const insertAt = insertBeforeId != null ? existing.indexOf(insertBeforeId) : -1
    const newIds = [...existing]
    newIds.splice(insertAt >= 0 ? insertAt : newIds.length, 0, nodeId)
    updated[targetColumnKey] = newIds

    setColumnNodeIds(updated)
    syncColumnJobOrders(updated)
    setDraggingNode(null)
  }

  /**
   * Unassigns a node that was dropped back onto the tree.
   *
   * Removes the node from its source column, re-syncs `jobOrder` for all remaining
   * column assignments, and clears the node's `jobOrder`, `positionInLine`, and
   * `preferenceDevice` fields.
   *
   * @param {Object} node - The dragged node carrying `_sourceColumnKey`
   */
  const handleNodeDroppedOnTree = (node) => {
    const sourceColumnKey = node._sourceColumnKey

    const updated = { ...columnNodeIds }
    if (sourceColumnKey) {
      updated[sourceColumnKey] = (updated[sourceColumnKey] ?? []).filter(id => id !== node.id)
    }
    syncColumnJobOrders(updated)
    setColumnNodeIds(updated)
    setDraggingNode(null)
    dispatchBalancedData({
      type: 'UPDATE_NODE_JOB_REFERENCE',
      nodeId: node.id,
      fields: { jobOrder: null, positionInLine: null, preferenceDevice: null }
    })
  }

  /**
   * Applies a new node order within a column after a reorder dialog confirmation
   * and synchronises `jobOrder` across all columns.
   *
   * @param {string} columnKey - The column whose order changed
   * @param {number[]} newOrderedIds - Node IDs in the confirmed new order
   */
  const handleNodesReordered = (columnKey, newOrderedIds) => {
    const updated = { ...columnNodeIds, [columnKey]: newOrderedIds }
    setColumnNodeIds(updated)
    syncColumnJobOrders(updated)
  }

  /**
   * Applies a selector change and requests a remap of the current assignment onto
   * the new context (HU-12). When the change can unassign jobs (`risky`) and there
   * are assigned nodes, it first asks for confirmation; otherwise it applies directly.
   *
   * @param {Function} action - Dispatches the SET_BALANCED_TYPE/OPTION change
   * @param {boolean} [risky] - Whether the change may unassign jobs (LINE target or fewer TYPE positions)
   */
  const requestChange = (action, risky = false) => {
    const hasAssignedNodes = Object.values(columnNodeIds).some(ids => ids.length > 0)
    if (risky && hasAssignedNodes) {
      setConfirmDialog({ open: true, pendingAction: action })
    } else {
      action()
      setRemapRequest(k => k + 1)
    }
  }

  /**
   * Applies the pending selector change and requests the remap (no reset): the
   * remap effect relocates what still fits and reports what was unassigned.
   */
  const handleConfirm = () => {
    confirmDialog.pendingAction?.()
    setRemapRequest(k => k + 1)
    setConfirmDialog({ open: false, pendingAction: null })
  }

  /** Dismisses the confirmation dialog without executing the pending action. */
  const handleCancel = () => {
    setConfirmDialog({ open: false, pendingAction: null })
  }

  const assignedNodeIds = new Set(Object.values(columnNodeIds).flat())

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', height: FULL_HEIGHT.BALANCED }}>
        <CircularProgress />
        <Typography>{t_('Loading data')}</Typography>
      </div>
    )
  }

  return (
    <div style={{ marginTop: '1rem', height: FULL_HEIGHT.BALANCED, overflow: 'hidden', display: 'flex' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateRows: 'auto 1fr', overflow: 'hidden' }}>
        <BalancedFormHeader
          dispatchBalancedData={dispatchBalancedData}
          balancedData={balancedData}
          disabled={!balancedData.editing}
          onDevicesLoaded={onDevicesLoaded}
          onGroupResolved={onGroupResolved}
          onRequestChange={requestChange}
        />
        <Group orientation='horizontal' style={{ overflow: 'hidden', marginTop: '0.3rem', minHeight: 0 }}>
          <Panel defaultSize='10rem' minSize='10rem' style={{ position: 'relative' }}>
            <BalancedFormTree
              balancedData={augmentedBalancedData}
              disabledNodeIds={assignedNodeIds}
              disabled={!balancedData.editing || !balancedData.selectedType}
              onNodeDroppedOnTree={handleNodeDroppedOnTree}
              onGroupDroppedOnTree={handleGroupDroppedOnTree}
              onNodeDragStart={handleNodeDragStart}
              onNodeDragEnd={handleNodeDragEnd}
              onNodeSelected={handleSelectNode}
              selectedNodeId={selectedNode?.id ?? null}
              multiSelection={multiSelection}
              onToggleMultiSelect={toggleMultiSelect}
            />
          </Panel>
          <PanelSeparator />
          <Panel style={{ position: 'relative', minWidth: 0 }}>
            <BalancedFormColumns
              balancedData={augmentedBalancedData}
              deviceGroupDevices={deviceGroupDevices}
              columnNodeIds={columnNodeIds}
              draggingNode={draggingNode}
              disabled={!balancedData.editing}
              columnPositionAllows={columnPositionAllows}
              onColumnDrop={handleColumnDrop}
              onNodesReordered={handleNodesReordered}
              onNodeDragStart={handleNodeDragStart}
              onNodeDragEnd={handleNodeDragEnd}
              onNodeSelected={handleSelectNode}
              selectedNodeId={selectedNode?.id ?? null}
              multiSelection={multiSelection}
              onToggleMultiSelect={toggleMultiSelect}
            />
          </Panel>
        </Group>
      </div>
      <Box
        onMouseDown={startInfoResize}
        title={t_('Resize')}
        sx={{
          width: '1rem',
          flexShrink: 0,
          cursor: 'col-resize',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '&:hover .info-resize-bar': { backgroundColor: 'primary.main', height: '4.5rem', width: '4px' }
        }}
      >
        <Box className='info-resize-bar' sx={{ width: '3px', height: '3rem', backgroundColor: 'divider', borderRadius: '3px', transition: 'all 0.2s ease-in-out', pointerEvents: 'none' }} />
      </Box>
      <div style={{ width: `${infoWidth}px`, flexShrink: 0, position: 'relative' }}>
        <BalancedFormJobInfo key={selectedNode?.id ?? 'none'} selectedNode={selectedNode} workflowId={balancedData.workflow?.id} collapsed={infoCollapsed} onToggleCollapse={toggleInfoCollapse} />
      </div>
      <SmartDialog
        setOpen={confirmDialog.open}
        title='default'
        message={t_('This change remaps the balancing and may unassign operations that no longer fit (incompatible technology or removed position). Do you want to continue?')}
        acceptCallback={handleConfirm}
        cancelCallback={handleCancel}
      />
    </div>
  )
}

export default BalancedForm
